import React from 'react';
import { motion } from 'framer-motion';
import { Switch } from '../components/ui/switch';

const LottoField = ({ 
  fieldNumber, 
  selectedNumbers, 
  isActive, 
  onToggle, 
  onNumberSelect 
}) => {
  const numbers = Array.from({ length: 49 }, (_, i) => i + 1);
  
  const handleNumberClick = (num) => {
    if (!isActive) return;
    
    if (selectedNumbers.includes(num)) {
      onNumberSelect(fieldNumber, selectedNumbers.filter(n => n !== num));
    } else if (selectedNumbers.length < 6) {
      onNumberSelect(fieldNumber, [...selectedNumbers, num].sort((a, b) => a - b));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: fieldNumber * 0.05 }}
      className={`ticket-field ${isActive ? 'active' : 'inactive'}`}
      data-testid={`lotto-field-${fieldNumber}`}
    >
      {/* Field Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-heading text-lg font-bold text-sol-green">
            Field {fieldNumber}
          </span>
          <span className="text-sm text-white/40 font-mono">
            {selectedNumbers.length}/6
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <Switch
            checked={isActive}
            onCheckedChange={() => onToggle(fieldNumber)}
            data-testid={`field-toggle-${fieldNumber}`}
          />
        </div>
      </div>

      {/* Selected Numbers Display */}
      <div className="flex gap-2 mb-4 min-h-[48px]">
        {selectedNumbers.map((num, idx) => (
          <motion.div
            key={num}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="lotto-ball lotto-ball-selected"
          >
            {num}
          </motion.div>
        ))}
        {Array.from({ length: 6 - selectedNumbers.length }).map((_, idx) => (
          <div key={`empty-${idx}`} className="lotto-ball border border-dashed border-white/20 bg-transparent">
            <span className="text-white/20">?</span>
          </div>
        ))}
      </div>

      {/* Number Grid */}
      <div className={`grid grid-cols-7 gap-1.5 ${!isActive ? 'pointer-events-none' : ''}`}>
        {numbers.map((num) => (
          <motion.button
            key={num}
            whileHover={isActive ? { scale: 1.1 } : {}}
            whileTap={isActive ? { scale: 0.95 } : {}}
            onClick={() => handleNumberClick(num)}
            disabled={!isActive}
            data-testid={`number-${fieldNumber}-${num}`}
            className={`
              lotto-ball text-xs
              ${selectedNumbers.includes(num) 
                ? 'lotto-ball-selected' 
                : 'lotto-ball-default'}
              ${!isActive ? 'opacity-30 cursor-not-allowed' : ''}
            `}
          >
            {num}
          </motion.button>
        ))}
      </div>

      {/* Quick Actions */}
      {isActive && (
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => {
              const randomNumbers = [];
              while (randomNumbers.length < 6) {
                const rand = Math.floor(Math.random() * 49) + 1;
                if (!randomNumbers.includes(rand)) {
                  randomNumbers.push(rand);
                }
              }
              onNumberSelect(fieldNumber, randomNumbers.sort((a, b) => a - b));
            }}
            className="text-xs text-sol-green/70 hover:text-sol-green transition-colors"
            data-testid={`quick-pick-${fieldNumber}`}
          >
            Quick Pick
          </button>
          <span className="text-white/20">|</span>
          <button
            onClick={() => onNumberSelect(fieldNumber, [])}
            className="text-xs text-white/40 hover:text-white/60 transition-colors"
            data-testid={`clear-${fieldNumber}`}
          >
            Clear
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default LottoField;
